"use client";

import { useTranslation } from "react-i18next";
import IntegrationTile from "@/components/integrations/marketplace/IntegrationTile";
import Icon, { ICON_TYPE } from "@/components/library/Icon";
import { useMetaCredentials } from "@/hooks/useMetaCredentials";
import MetaSetupPanel from "./MetaSetupPanel";

/**
 * Marketplace tile for Facebook Lead Ads. The hook lives here rather than in the
 * panel because both need the same rows — two instances would fetch twice and
 * could disagree.
 */
export default function MetaIntegrationCard() {
  const { t } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const meta = useMetaCredentials();

  const { status, credentials } = meta;
  const connectedCount = credentials.length;
  const connected = connectedCount > 0;
  // A failed read is not "not connected": offering Connect over live Pages would
  // read as an invitation to set the integration up a second time.
  const manageable = connected || status === "loadError";

  return (
    <IntegrationTile
      testId="meta-integration-card"
      closeTestId="meta-setup-close"
      icon={
        <Icon
          icon="facebook-f"
          type={ICON_TYPE.BRAND}
          className="w-5 h-5 text-[#1877F2]"
        />
      }
      iconClassName="bg-blue-100 dark:bg-blue-900"
      title={t("metaIntegration.title")}
      connected={connected}
      actionDisabled={status === "loading"}
      actionLabel={manageable ? t("metaIntegration.manage") : ct("connect")}
      dialogTitle={t("metaIntegration.dialogTitle")}
      dialogDescription={t("metaIntegration.dialogDescription")}
      description={
        <>
          {status === "loading" && <p className="text-600 text-sm">{ct("loading")}</p>}
          {status === "loadError" && (
            <p className="text-sm text-500">{t("metaIntegration.loadError")}</p>
          )}
          {status === "loaded" && (
            <p className="text-sm text-600">
              {connected
                ? t("metaIntegration.connectedDescription", { count: connectedCount })
                : t("metaIntegration.disconnectedDescription")}
            </p>
          )}
        </>
      }
    >
      <MetaSetupPanel meta={meta} />
    </IntegrationTile>
  );
}
